import React, { useState } from 'react';
import { Moon, Heart, Shield, Star, Clock, Baby, Book, MessageCircle, Menu, X, Sun, Mail, Phone } from 'lucide-react';
import homeImage from './home-image.jpg'; // Image plus claire à importer
import NavBar from './NavBar'; // Import du composant NavBar

const NurseryHome = () => {
  const [hoveredService, setHoveredService] = useState(null);
  const [hoveredActivity, setHoveredActivity] = useState(null);
  const [selectedFaq, setSelectedFaq] = useState(null);
  const [showContactOptions, setShowContactOptions] = useState(false);

  const services = [
    {
      icon: Moon,
      title: "Garde régulière planifiée",
      schedule: "Disponible 7j/7, du lundi au dimanche, y compris pendant les vacances scolaires et jours fériés. Horaires flexibles avec possibilité de nuits de 8 à 12 heures (début possible dès 19h avec prolongation matinale). Service proposé sur Paris et en banlieue.",
      description: "La garde régulière planifiée est un service de garde d'enfants à domicile caractérisé par sa récurrence et sa prévisibilité. Ce type de prestation répond aux besoins des familles ayant un emploi du temps stable et des besoins de garde constants."
    },
    {
      icon: Clock,
      title: "Garde occasionnelle",
      schedule: "Service disponible tous les jours de la semaine, week-ends inclus, ainsi que pendant les périodes de vacances scolaires et jours fériés. Les interventions peuvent être programmées avec flexibilité selon vos besoins ponctuels, y compris en soirée. Déplacements assurés sur Paris et sa banlieue.",
      description: "La garde occasionnelle à domicile est un service flexible de prise en charge des enfants, adapté aux besoins ponctuels et variables des familles. Cette solution répond parfaitement aux situations imprévues ou aux activités planifiées occasionnellement."
    },
    {
      icon: Baby,
      title: "Garde Spéciale",
      schedule: "Prestations assurées du lundi au dimanche sans interruption, y compris durant les vacances scolaires et jours fériés. Pour les gardes de nuit, durée adaptable de 8 à 12 heures avec possibilité de commencer dès 19h et d'étendre la présence le matin. Intervention possible partout à Paris et en banlieue.",
      description: "Les services spécialisés de garde d'enfants à domicile répondent à des besoins spécifiques ou des situations particulières qui nécessitent une expertise ou une attention adaptée. Ces prestations sur mesure vont au-delà de la simple surveillance pour offrir un accompagnement personnalisé."
    }
  ];

  const faqs = [
    {
      question: "Quel est l'âge minimum accepté ?",
      answer: "Nous accueillons les bébés dès la sortie de la maternité."
    },
    {
      question: "Comment se passe une nuit type pour un bébé ?",
      answer: "À la sortie de maternité, nous assurons un relais complet avec les parents incluant une transmission détaillée des besoins du bébé. Nous nous occupons de l'alimentation selon votre choix (allaitement avec biberons préparés ou formule), accompagnement du sommeil, surveillance des reflux ou coliques (soins, confort et surveillance continue), et adaptation personnalisée au rythme de votre bébé."
    },
    {
      question: "Comment se passe une nuit type pour un enfant de 3 à 6 ans ?",
      answer: "20h: Accueil et dîner léger, 20h30: Activités calmes, 21h: Routine du coucher, surveillance continue toute la nuit."
    },
    {
      question: "Quelles sont vos certifications ?",
      answer: "Nos professionnelles sont certifiées en puériculture, premiers secours pédiatriques et développement de l'enfant."
    }
  ];

  const activities = [
    {
      title: "Routine du Soir",
      description: "Activités douces et apaisantes pour préparer au sommeil",
      time: ""
    },
    {
      title: "Mise en condition",
      description: "Installation d'un cadre propice au sommeil",
      time: ""
    },
    {
      title: "Diner Équilibré",
      description: "Repas varié et nutritif",
      time: ""
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation avec le composant NavBar */}
      <NavBar />

      {/* Hero Section avec fond bleu ciel et nuages animés */}
      <div className="relative bg-sky-100 overflow-hidden">
        {/* Nuages animés */}
        <div className="absolute inset-0">
          <div className="cloud cloud-1" />
          <div className="cloud cloud-2" />
          <div className="cloud cloud-3" />
        </div>

        {/* Contenu de la section hero */}
        <div className="relative z-10 max-w-4xl mx-auto text-center px-4 py-12 md:py-20">
          <h1 className="text-3xl md:text-5xl font-bold text-slate-800 mb-4 md:mb-6">
            Etoile Veilleuse - Jour & Nuit, nous veillons sur votre bébé  
          </h1>
          <p className="text-lg md:text-xl text-slate-600">
            Nurses expérimentées pour les gardes à domicile - Un soutien bienveillant pour votre bébé et votre famille 
          </p>
        </div>
      </div>

      {/* Section image principale avec texte */}
      <div className="max-w-6xl mx-auto px-4 py-12 relative">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="md:flex">
            <div className="md:w-1/2 relative h-64 md:h-auto">
              <img 
                src={homeImage} 
                alt="Bébé qui dort paisiblement" 
                className="w-full h-full object-cover absolute inset-0"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent flex items-end">
                <div className="p-6 text-white">
                  <span className="text-amber-400 text-lg">✦</span>
                  <span className="ml-2 font-medium">Un environnement sécurisé et apaisant</span>
                </div>
              </div>
            </div>
            <div className="md:w-1/2 p-6 md:p-8 bg-white">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">⭐ Bienvenue chez Étoile Veilleuse – Le confort et le sommeil de votre bébé, notre priorité</h2>
              <p className="text-slate-600 mb-4">
                Chez Étoile Veilleuse, nous proposons un service de garde de nuit pour bébés dans un environnement sécurisé, chaleureux et apaisant. Notre mission : aider les tout-petits à passer des nuits paisibles et offrir aux parents une tranquillité d’esprit totale.

Nos professionnelles de la petite enfance, formées aux besoins spécifiques des nouveau-nés et jeunes enfants, assurent une surveillance continue tout au long de la nuit. Chaque bébé est accompagné avec douceur et bienveillance, dans le respect de son rythme de sommeil et de ses besoins individuels.
              </p>
              
              {/* Section avec titre et paragraphe complémentaire */}
              <div className="mt-6 space-y-4">
                <div className="flex items-center text-slate-800">
                  <Moon className="text-amber-400 w-5 h-5 mr-2" />
                  <span className="font-medium">Des nuits sereines pour toute la famille</span>
                </div>
                
                <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-amber-400">
                  <p className="text-slate-600 text-sm md:text-base">
                    Nous créons un univers nocturne apaisant avec des veilleuses douces, des berceuses relaxantes et des routines personnalisées qui favorisent un endormissement naturel et sécurisé. Grâce à notre approche centrée sur le bien-être de l’enfant, votre bébé bénéficie de conditions idéales pour s’endormir et se réveiller en douceur.

Faites confiance à Étoile Veilleuse, votre partenaire pour des nuits calmes, un sommeil réparateur pour votre bébé, et des parents enfin reposés.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section Services Spécialisés */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-8 md:mb-12">Découvrez Nos Services</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-16">
          <a 
            href="/nurse-nuit"
            className="bg-gradient-to-br from-blue-900 to-blue-700 text-white rounded-lg p-8 shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-xl"
          >
            <Moon className="w-16 h-16 mb-4 mx-auto text-amber-300" />
            <h3 className="text-xl font-bold mb-3 text-center">Nurse de Nuit</h3>
            <p className="text-center text-blue-100">
              Service de garde nocturne professionnel pour des nuits paisibles
            </p>
          </a>
          
          <a 
            href="/nurse-jour"
            className="bg-gradient-to-br from-amber-400 to-orange-500 text-white rounded-lg p-8 shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-xl"
          >
            <Sun className="w-16 h-16 mb-4 mx-auto text-white" />
            <h3 className="text-xl font-bold mb-3 text-center">Nurse de Jour</h3>
            <p className="text-center text-amber-50">
              Accompagnement diurne personnalisé pour votre bébé
            </p>
          </a>
          
          <a 
            href="/accompagnement-parental"
            className="bg-gradient-to-br from-rose-500 to-pink-600 text-white rounded-lg p-8 shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-xl"
          >
            <Heart className="w-16 h-16 mb-4 mx-auto text-pink-100" />
            <h3 className="text-xl font-bold mb-3 text-center">Accompagnement Parental</h3>
            <p className="text-center text-rose-50">
              Soutien et conseils pour les nouveaux parents
            </p>
          </a>
        </div>
      </div>

      {/* Prestations */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-8 md:mb-12">Nos Prestations</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, index) => (
            <div 
              key={index} 
              className="bg-white shadow-md rounded-lg p-6 transition-all duration-300 hover:shadow-lg hover:scale-105"
              onMouseEnter={() => setHoveredService(index)}
              onMouseLeave={() => setHoveredService(null)}
            >
              <service.icon className={`w-10 h-10 md:w-12 md:h-12 mb-4 ${
                hoveredService === index ? 'text-amber-400' : 'text-slate-600'
              } transition-colors duration-300`} />
              <h3 className="text-lg md:text-xl font-semibold text-slate-800 mb-2">{service.title}</h3>
              <div className="text-amber-500 mb-2">{service.time}</div>
              <div className="text-lg font-semibold text-slate-800 mb-2">{service.price}</div>
              <p className="text-sm md:text-base text-slate-600">{service.description}</p>
              {service.schedule && (
                <>
                 <br /><br />
                 <span className="font-medium">Horaires et disponibilités : </span>
                 {service.schedule}
              </>
             )}
         </div>
          ))}
        </div>
      </div>

      {/* Activités */}
      <div id="activites" className="max-w-6xl mx-auto px-4 py-12 md:py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-8 md:mb-12">Programme Type</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {activities.map((activity, index) => (
            <div 
              key={index} 
              className="bg-slate-50 shadow-md rounded-lg p-6 transition-all duration-300 hover:shadow-lg"
              onMouseEnter={() => setHoveredActivity(index)}
              onMouseLeave={() => setHoveredActivity(null)}
            >
              <Clock className={`w-10 h-10 md:w-12 md:h-12 mb-4 ${
                hoveredActivity === index ? 'text-amber-400' : 'text-slate-600'
              } transition-colors duration-300`} />
              <div className="text-amber-500 mb-2">{activity.time}</div>
              <h3 className="text-lg md:text-xl font-semibold text-slate-800 mb-2">{activity.title}</h3>
              <p className="text-sm md:text-base text-slate-600">{activity.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Points forts */}
      <div className="max-w-6xl mx-auto px-4 py-12 md:py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-8 md:mb-12">Nos Points Forts</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          <div className="bg-slate-50 shadow-md rounded-lg p-6 transition-all duration-300 hover:shadow-lg">
            <Heart className="text-rose-400 w-10 h-10 md:w-12 md:h-12 mb-4" />
            <h3 className="text-lg md:text-xl font-semibold text-slate-800 mb-2">Attention Personnalisée</h3>
            <p className="text-sm md:text-base text-slate-600">Chaque bébé reçoit des soins adaptés à ses besoins spécifiques</p>
          </div>
          <div className="bg-slate-50 shadow-md rounded-lg p-6 transition-all duration-300 hover:shadow-lg">
            <Shield className="text-emerald-400 w-10 h-10 md:w-12 md:h-12 mb-4" />
            <h3 className="text-lg md:text-xl font-semibold text-slate-800 mb-2">Sécurité Maximale</h3>
            <p className="text-sm md:text-base text-slate-600">Surveillance 24/7 et personnel qualifié en puériculture</p>
          </div>
          <div className="bg-slate-50 shadow-md rounded-lg p-6 transition-all duration-300 hover:shadow-lg">
            <Star className="text-amber-400 w-10 h-10 md:w-12 md:h-12 mb-4" />
            <h3 className="text-lg md:text-xl font-semibold text-slate-800 mb-2">Environnement Apaisant</h3>
            <p className="text-sm md:text-base text-slate-600">Ambiance calme, berceuses et câlins pour un sommeil serein</p>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="max-w-4xl mx-auto px-4 py-12 md:py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-8 md:mb-12">Questions Fréquentes</h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className="bg-slate-50 shadow-md rounded-lg overflow-hidden"
            >
              <button
                className="w-full px-4 md:px-6 py-3 md:py-4 text-left text-slate-800 font-semibold flex justify-between items-center text-sm md:text-base"
                onClick={() => setSelectedFaq(selectedFaq === index ? null : index)}
              >
                {faq.question}
                <span className={`transform transition-transform ${selectedFaq === index ? 'rotate-180' : ''}`}>
                  ▼
                </span>
              </button>
              {selectedFaq === index && (
                <div className="px-4 md:px-6 py-3 md:py-4 text-sm md:text-base text-slate-600 border-t border-slate-200">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Contact Button avec menu déroulant */}
      <div className="fixed bottom-6 left-0 right-0 px-4 text-center">
        <a 
          href="/legal"
          className="inline-block bg-slate-200 px-4 py-2 rounded-full text-slate-600 text-sm hover:bg-slate-300 shadow-md transform transition-all duration-300 hover:scale-105 mr-4"
        >
          Protection & CGU
        </a>
        
        <div 
          className="inline-block relative"
          onMouseEnter={() => setShowContactOptions(true)}
          onMouseLeave={() => setShowContactOptions(false)}
        >
          <button className="bg-amber-400 px-6 py-3 rounded-full hover:bg-amber-500 text-slate-800 shadow-lg transform transition-all duration-300 hover:scale-105">
            <MessageCircle className="w-5 h-5 inline mr-2" />
            Contactez-nous
          </button>
          
          {/* Menu déroulant */}
          {showContactOptions && (
            <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-xl overflow-hidden min-w-[200px]">
              <a 
                href="mailto:etoileveilleuse@gmail.com"
                className="flex items-center px-4 py-3 hover:bg-amber-50 transition-colors text-slate-700"
              >
                <Mail className="w-5 h-5 mr-3 text-amber-500" />
                <span>Email</span>
              </a>
              <a 
                href="https://wa.me/33609321931"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center px-4 py-3 hover:bg-amber-50 transition-colors text-slate-700 border-t border-slate-100"
              >
                <MessageCircle className="w-5 h-5 mr-3 text-green-500" />
                <span>WhatsApp</span>
              </a>
            </div>
          )}
        </div>
      </div>
      
      {/* Styles pour les nuages */}
      <style jsx>{`
        .cloud {
          position: absolute;
          background: rgba(255, 255, 255, 0.9);
          border-radius: 100px;
          animation: float 20s infinite linear;
        }

        .cloud-1 {
          width: 200px;
          height: 60px;
          top: 20%;
          left: -200px;
          animation-duration: 25s;
        }

        .cloud-2 {
          width: 150px;
          height: 45px;
          top: 40%;
          left: -150px;
          animation-duration: 30s;
          animation-delay: -10s;
        }

        .cloud-3 {
          width: 180px;
          height: 54px;
          top: 60%;
          left: -180px;
          animation-duration: 35s;
          animation-delay: -5s;
        }

        @keyframes float {
          from { transform: translateX(-100%); }
          to { transform: translateX(100vw); }
        }

        @media (max-width: 768px) {
          .cloud {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default NurseryHome;